import makeRequest from "./makeRequest";
import getRequest from "../utils/requests";
import { getCustomerId } from "../utils/identity";
import { GET, POST } from "../constants";
import getConfig from "../config";
import type {
  MySizeProfile,
  BrandsResponse,
  RecommendedBrandSize,
  SizeGuideRecommendation,
} from "./mySizeProfileApiTypes";

const getSizeProfileUrl = (customerId: string) => {
  const { sizeProfileApiUrl } = getConfig() as { sizeProfileApiUrl: string };
  return `${sizeProfileApiUrl}/${encodeURIComponent(customerId)}`;
};

const getCustomerSizeGuideUrl = (
  customerId: string,
  productId: number,
  language?: string,
) => {
  const { sizeProfileApiUrl } = getConfig() as { sizeProfileApiUrl: string };

  return `${sizeProfileApiUrl}/${encodeURIComponent(
    customerId,
  )}/SizeGuide?productId=${productId}${
    language ? `&language=${encodeURIComponent(language)}` : ""
  }`;
};

const getBrandsUrl = (sizeSchema: string, language?: string) => {
  const { sizeProfileApiUrl } = getConfig() as { sizeProfileApiUrl: string };
  const queryParams = new URLSearchParams({
    sizeSchema,
    ...(language ? { language } : {}),
  });

  return `${sizeProfileApiUrl}/Brands?${queryParams.toString()}`;
};

const handleErrorReturnValue = (error: Error) => {
  throw error;
};

const getResolvedCustomerId = async (customerId?: string) =>
  customerId ?? getCustomerId();

type BackendSizeGuideResponse = {
  sizeProfileId?: string | null;
  sizeProfile?: string | null;
  brandSizes?: RecommendedBrandSize[] | null;
  sizeGuideMessage?: string | null;
  recommendationMessage?: string | null;
};

const normalizeProfileResponse = (
  profile: MySizeProfile | null,
): MySizeProfile | null => {
  if (!profile) {
    return null;
  }

  return {
    ...profile,
    sizeProfileId: profile.sizeProfileId ?? null,
    legLength: profile.legLength ?? null,
  };
};

const normalizeSizeGuideResponse = (
  response: BackendSizeGuideResponse | null,
): SizeGuideRecommendation | null => {
  if (!response) {
    return null;
  }

  return {
    sizeProfileId: response.sizeProfileId ?? response.sizeProfile ?? null,
    brandSizes: response.brandSizes ?? null,
    sizeGuideMessage: response.sizeGuideMessage ?? null,
    recommendationMessage: response.recommendationMessage ?? null,
  };
};

export const fetchBrands = async (
  sizeSchema: string,
  language?: string,
): Promise<BrandsResponse> => {
  return makeRequest({
    params: undefined,
    url: getBrandsUrl(sizeSchema, language),
    timeout: undefined,
    headers: undefined,
    requestType: "MY_SIZE_PROFILE_API",
    apiName: "MySizeProfileBrands",
    caller: "fetchBrands",
    mapData: undefined,
    body: undefined,
    handleErrorReturnValue,
    method: GET,
  });
};

export const fetchMySizeProfile = async (
  customerId?: string,
): Promise<MySizeProfile | null> => {
  const resolvedCustomerId = await getResolvedCustomerId(customerId);
  const { sizeProfileApiTimeout } = getConfig() as {
    sizeProfileApiTimeout: number;
  };

  try {
    const response = await getRequest()[GET]({
      url: getSizeProfileUrl(resolvedCustomerId),
      timeout: sizeProfileApiTimeout,
      additionalLoggingParams: {
        requestType: "MY_SIZE_PROFILE_API",
      },
    });
    return normalizeProfileResponse(response.data as MySizeProfile | null);
  } catch (error: unknown) {
    const httpError = error as Error & { response?: { status?: number } };
    if (httpError?.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export async function saveMySizeProfile(
  customerIdOrSizeProfileId: string,
  sizeProfileIdOrLegLength: string | number | null,
  legLengthOrUndefined?: number | null,
  brandId?: number,
): Promise<MySizeProfile> {
  const usesExplicitCustomerId = typeof sizeProfileIdOrLegLength === "string";
  const customerId = usesExplicitCustomerId
    ? customerIdOrSizeProfileId
    : await getCustomerId();
  const sizeProfileId = usesExplicitCustomerId
    ? sizeProfileIdOrLegLength
    : customerIdOrSizeProfileId;
  const legLength = usesExplicitCustomerId
    ? (legLengthOrUndefined ?? null)
    : sizeProfileIdOrLegLength;
  const { sizeProfileApiTimeout } = getConfig() as {
    sizeProfileApiTimeout: number;
  };

  return makeRequest({
    params: undefined,
    url: getSizeProfileUrl(customerId),
    timeout: sizeProfileApiTimeout,
    headers: undefined,
    requestType: "MY_SIZE_PROFILE_API",
    apiName: "MySizeProfile",
    caller: "saveMySizeProfile",
    mapData: undefined,
    body: {
      sizeProfileId,
      legLength,
      ...(usesExplicitCustomerId && brandId !== undefined ? { brandId } : {}),
    },
    handleErrorReturnValue,
    method: POST,
  });
}

export const fetchCustomerSizeGuide = async (
  customerId: string,
  productId: number,
  language?: string,
): Promise<SizeGuideRecommendation | null> => {
  const { sizeProfileApiTimeout } = getConfig() as {
    sizeProfileApiTimeout: number;
  };

  try {
    const response = await getRequest()[GET]({
      url: getCustomerSizeGuideUrl(customerId, productId, language),
      timeout: sizeProfileApiTimeout,
      additionalLoggingParams: {
        requestType: "MY_SIZE_PROFILE_API",
      },
    });

    return normalizeSizeGuideResponse(
      response.data as BackendSizeGuideResponse | null,
    );
  } catch (error: unknown) {
    const httpError = error as Error & { response?: { status?: number } };

    if (httpError?.response?.status === 404) {
      return null;
    }

    throw error;
  }
};
